import * as THREE from 'three';
import { EnemyFactory, type EnemyType } from '../assets/modelFactories/EnemyFactory';
import { ENEMY_STATS, Enemy, type EnemySpawnConfig } from '../entities/Enemy';

export type Wave = {
  index: number;
  title: string;
  subtitle: string;
  enemies: { type: EnemyType; count: number; interval: number; path?: number | 'random' }[];
  delayBefore: number;
  bombardment?: { count: number; delay: number; interval: number };
};

export function createWaves(): Wave[] {
  return [
    {
      index: 1,
      title: '第一波',
      subtitle: '步兵侦察队',
      enemies: [{ type: 'infantry', count: 16, interval: 1.2, path: 'random' }],
      delayBefore: 2,
    },
    {
      index: 2,
      title: '第二波',
      subtitle: '两翼包抄',
      enemies: [
        { type: 'infantry', count: 14, interval: 1.1, path: 1 },
        { type: 'infantry', count: 14, interval: 1.1, path: 2 },
      ],
      delayBefore: 2,
      bombardment: { count: 4, delay: 1.5, interval: 1.4 },
    },
    {
      index: 3,
      title: '第三波',
      subtitle: '登陆艇与伞兵',
      enemies: [
        { type: 'landingcraft', count: 5, interval: 2.8, path: 'random' },
        { type: 'paratrooper', count: 7, interval: 1.6 },
        { type: 'infantry', count: 16, interval: 0.9, path: 'random' },
      ],
      delayBefore: 2,
      bombardment: { count: 6, delay: 1, interval: 1.2 },
    },
    {
      index: 4,
      title: '第四波',
      subtitle: '坦克前锋',
      enemies: [
        { type: 'tank', count: 5, interval: 3.8, path: 'random' },
        { type: 'infantry', count: 20, interval: 0.75, path: 'random' },
      ],
      delayBefore: 2,
      bombardment: { count: 8, delay: 1, interval: 1.1 },
    },
    {
      index: 5,
      title: '第五波',
      subtitle: '空中袭扰',
      enemies: [
        { type: 'plane', count: 6, interval: 2.2 },
        { type: 'landingcraft', count: 6, interval: 2.2, path: 'random' },
        { type: 'infantry', count: 22, interval: 0.65, path: 'random' },
      ],
      delayBefore: 2,
      bombardment: { count: 10, delay: 0.5, interval: 0.9 },
    },
    {
      index: 6,
      title: '第六波',
      subtitle: '装甲主力',
      enemies: [
        { type: 'tank', count: 7, interval: 3.0, path: 'random' },
        { type: 'paratrooper', count: 10, interval: 1.3 },
        { type: 'landingcraft', count: 6, interval: 2.4, path: 'random' },
      ],
      delayBefore: 2,
      bombardment: { count: 12, delay: 0.5, interval: 0.8 },
    },
    {
      index: 7,
      title: '第七波',
      subtitle: '人海、钢铁与天空',
      enemies: [
        { type: 'plane', count: 9, interval: 1.8 },
        { type: 'infantry', count: 32, interval: 0.45, path: 'random' },
        { type: 'tank', count: 7, interval: 3.0, path: 'random' },
        { type: 'paratrooper', count: 12, interval: 1.1 },
      ],
      delayBefore: 2,
      bombardment: { count: 18, delay: 0.5, interval: 0.7 },
    },
    {
      index: 8,
      title: '最终波次',
      subtitle: '总攻',
      enemies: [
        { type: 'tank', count: 11, interval: 2.6, path: 'random' },
        { type: 'landingcraft', count: 9, interval: 2.0, path: 'random' },
        { type: 'plane', count: 9, interval: 2.0 },
        { type: 'paratrooper', count: 15, interval: 0.9 },
        { type: 'infantry', count: 38, interval: 0.4, path: 'random' },
      ],
      delayBefore: 3,
      bombardment: { count: 26, delay: 0.5, interval: 0.55 },
    },
  ];
}

type SpawnQueueItem = {
  type: EnemyType;
  remaining: number;
  interval: number;
  timer: number;
  path?: number | 'random';
};

export class WaveSystem {
  private readonly waves: Wave[];
  private waveIndex = -1;
  private state: 'idle' | 'delay' | 'spawning' | 'waiting' | 'complete' = 'idle';
  private stateTimer = 0;
  private queue: SpawnQueueItem[] = [];
  private shellRemaining = 0;
  private shellTimer = 0;
  private shellInterval = 0;
  private onWaveStartCallback?: (wave: Wave) => void;
  private onEnemySpawnCallback?: (enemy: Enemy) => void;
  private onShellDropCallback?: () => void;

  constructor(
    private readonly enemyFactory: EnemyFactory,
    private readonly paths: THREE.CatmullRomCurve3[],
    private readonly spawnPositions: THREE.Vector3[],
    private readonly objectivePosition: THREE.Vector3,
    private readonly validGround: THREE.Box2,
    waves?: Wave[],
  ) {
    this.waves = waves ?? createWaves();
  }

  get totalWaves(): number {
    return this.waves.length;
  }

  get currentWaveIndex(): number {
    return this.waveIndex;
  }

  get isComplete(): boolean {
    return this.state === 'complete';
  }

  get isSpawning(): boolean {
    return this.state === 'spawning';
  }

  get isWaiting(): boolean {
    return this.state === 'waiting';
  }

  startNextWave(): void {
    if (this.state === 'complete' || this.waveIndex >= this.waves.length - 1) return;

    this.waveIndex += 1;
    const wave = this.waves[this.waveIndex];
    this.state = 'delay';
    this.stateTimer = wave.delayBefore;
    this.shellRemaining = wave.bombardment?.count ?? 0;
    this.shellTimer = wave.bombardment?.delay ?? 0;
    this.shellInterval = wave.bombardment?.interval ?? 0;
    this.queue = wave.enemies.map((e) => ({
      type: e.type,
      remaining: e.count,
      interval: e.interval,
      timer: 0,
      path: e.path,
    }));

    this.onWaveStartCallback?.(wave);
  }

  update(delta: number): Enemy[] {
    const spawned: Enemy[] = [];

    if (this.shellRemaining > 0) {
      this.shellTimer -= delta;
      while (this.shellTimer <= 0 && this.shellRemaining > 0) {
        this.shellTimer += this.shellInterval;
        this.shellRemaining -= 1;
        this.onShellDropCallback?.();
      }
    }

    switch (this.state) {
      case 'delay':
        this.stateTimer -= delta;
        if (this.stateTimer <= 0) {
          this.state = 'spawning';
        }
        break;
      case 'spawning':
        for (const item of this.queue) {
          item.timer -= delta;
          while (item.timer <= 0 && item.remaining > 0) {
            item.timer += item.interval;
            item.remaining -= 1;
            const enemy = this.spawnEnemy(item.type, item.path);
            spawned.push(enemy);
          }
        }

        if (this.queue.every((item) => item.remaining === 0)) {
          this.state = 'waiting';
        }
        break;
      case 'waiting':
        if (this.waveIndex >= this.waves.length - 1) {
          this.state = 'complete';
        }
        break;
    }

    return spawned;
  }

  onWaveStart(callback: (wave: Wave) => void): void {
    this.onWaveStartCallback = callback;
  }

  onEnemySpawn(callback: (enemy: Enemy) => void): void {
    this.onEnemySpawnCallback = callback;
  }

  onShellDrop(callback: () => void): void {
    this.onShellDropCallback = callback;
  }

  reset(): void {
    this.waveIndex = -1;
    this.state = 'idle';
    this.stateTimer = 0;
    this.queue = [];
    this.shellRemaining = 0;
    this.shellTimer = 0;
    this.shellInterval = 0;
  }

  private spawnEnemy(type: EnemyType, pathHint?: number | 'random'): Enemy {
    const model = this.enemyFactory.create(type);
    const stats = ENEMY_STATS[type];

    let config: EnemySpawnConfig;
    if (stats.movement === 'ground') {
      const index = this.resolvePathIndex(pathHint);
      config = { path: this.paths[index] };
    } else if (stats.movement === 'air') {
      const start = this.spawnPositions[Math.floor(Math.random() * this.spawnPositions.length)].clone();
      start.y = 3;
      const target = this.objectivePosition.clone();
      target.y = 3;
      config = { start, target };
    } else {
      const start = this.findParatrooperDrop();
      const target = this.objectivePosition.clone();
      target.y = 0;
      config = { start, target };
    }

    const enemy = new Enemy(type, model, config);
    this.onEnemySpawnCallback?.(enemy);
    return enemy;
  }

  private resolvePathIndex(pathHint?: number | 'random'): number {
    if (typeof pathHint === 'number' && pathHint >= 0 && pathHint < this.paths.length) {
      return pathHint;
    }
    return Math.floor(Math.random() * this.paths.length);
  }

  private findParatrooperDrop(): THREE.Vector3 {
    const min = this.validGround.min;
    const max = this.validGround.max;

    for (let i = 0; i < 20; i++) {
      const x = min.x + Math.random() * (max.x - min.x);
      const z = min.y + Math.random() * (max.y - min.y);
      const point = new THREE.Vector3(x, 0, z);

      if (point.distanceToSquared(this.objectivePosition) < 9) continue;
      if (this.isNearAnyPath(point)) continue;

      return point;
    }

    return this.spawnPositions[0].clone();
  }

  private isNearAnyPath(point: THREE.Vector3): boolean {
    for (const path of this.paths) {
      for (let t = 0; t <= 1; t += 0.03) {
        const p = path.getPointAt(t);
        if (point.distanceTo(new THREE.Vector3(p.x, 0, p.z)) < 1.3) {
          return true;
        }
      }
    }
    return false;
  }
}
